import { getUpcomingEventById, getUpcomingEvents } from 'api/cms';
import { H4 } from 'components/headings';
import Layout from 'components/layout';
import RichText from 'components/rich-text';
import SEO from 'components/seo';
import Spinner from 'components/spinner';
import { ReminderContent } from 'components/ui/content-section';
import { Text } from 'components/ui/text';
import { format } from 'date-fns';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import Custom404 from 'pages/404';
import { useQuery } from 'react-query';
import { useEffectOnce } from 'react-use';
import AnalyticsService, { ScreenNames } from 'services/analytics';
import { UpcomingEvent } from 'types/sanity';
import { backgroundImages } from 'utils/config';
import { formatReminderDate } from 'utils/helpers';
import { protectRoute } from 'utils/protect-route';
import APP_ROUTES from 'utils/routes';

interface Props {
  data?: UpcomingEvent;
  errorCode?: number;
}

const Reminder: NextPage<Props> = (props) => {
  const router = useRouter();
  const { id } = router.query;
  const { data, isLoading } = useQuery(
    [id],
    () => getUpcomingEventById(id as string),
    {
      initialData: props.data,
    }
  );
  const reminderNotFound = !isLoading && (!data || !data?._id);

  useEffectOnce(() => {
    if (!reminderNotFound) {
      AnalyticsService.trackScreen(ScreenNames.reminderDetail);
    }
  });

  if (props.errorCode || reminderNotFound) {
    return <Custom404 />;
  }

  return (
    <>
      <SEO title={data?.title || 'Loading...'} />
      <Layout mainBackground={backgroundImages.treesBurn}>
        <ReminderContent backLink={APP_ROUTES.account}>
          {isLoading && <Spinner color='black' />}
          {data && data._id && (
            <>
              <Text as='time'>
                {formatReminderDate(data.startDate, data.endDate)}
              </Text>
              <H4 as='h1'>{data.title}</H4>
              <RichText blocks={data.body} />
            </>
          )}
        </ReminderContent>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const id = params?.id as string;

  const data = await getUpcomingEventById(id);

  // Page doesn't exist
  if (!data) {
    return {
      props: { errorCode: 404 },
    };
  }

  return {
    props: {
      data,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  const upcomingEvents = await getUpcomingEvents(dateStr);
  const paths =
    upcomingEvents?.map((event: UpcomingEvent) => ({
      params: {
        id: event._id,
      },
    })) || [];

  return {
    paths,
    fallback: false,
  };
};

export default protectRoute(Reminder);
